import React, {
  type ComponentProps,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DragDropProvider } from "@dnd-kit/react";
import LoadingIcon from "../../../../../../components/Loaders/LoadingIcon";
import SegmentedControl from "../../../../../../components/Inputs/SegmentedControl";
import WarningTriangleIcon from "../../../../../../resources/route-builder/warning-triangle.svg";
import type { ViewMode } from "../../types/routeBuilderTypes";
import {
  UNASSIGNED_ROUTE_ID,
  useRouteBuilder,
} from "../../context/RouteBuilderContext";
import { unassignedVisits } from "../../lib/addUnassignedCatalog";
import { getStopDropIndex } from "../../lib/stopDropPreview";
import { locatePropertyVisit } from "../../lib/sessionOps";
import RoutesSidebar from "./views/RoutesSidebar";
import SelectedRoutePanel from "./views/SelectedRoutePanel";
import ListBoardView from "./views/ListBoardView";
import { ROUTE_BOARD_COLUMN_SORT_TYPE } from "./views/RouteBoardColumn";
import RouteFocusBar from "./RouteFocusBar";
import RouteBuilderMap from "./views/RouteBuilderMap";
import UnassignedPool from "./views/UnassignedPool";

type DragEndEvent = Parameters<
  NonNullable<ComponentProps<typeof DragDropProvider>["onDragEnd"]>
>[0];

function parseStopId(id: string | number | undefined) {
  if (id == null) return null;
  const str = String(id);
  const sep = str.indexOf(":");
  if (sep < 0) return null;
  return {
    routeId: str.slice(0, sep),
    propertyVisitId: str.slice(sep + 1),
  };
}

/**
 * Start with Unassigned open when the plan has leftovers, or when the route is
 * built by hand (Create Route Manually), since its stops come from that list.
 */
function opensUnassigned(
  session: ReturnType<typeof useRouteBuilder>["session"],
) {
  return (
    (session?.unroutedJobs?.length ?? 0) > 0 || session?.origin === "blank"
  );
}

export default function AdjustWorkspace() {
  const {
    session,
    viewMode,
    setViewMode,
    setSelectedRouteId,
    dirtyRouteIds,
    reoptimizeAll,
    movePropertyVisit,
    reorderRoutes,
    registerOpenUnassignedPanel,
    resetMoveToUnassignedConfirm,
    isBusy,
    isRefreshingProperties,
    optimizationBlocked,
  } = useRouteBuilder();

  const [unassignedOpen, setUnassignedOpen] = useState(() =>
    opensUnassigned(session),
  );
  const [unassignedSearch, setUnassignedSearch] = useState("");
  const poolVisits = useMemo(
    () => (session ? unassignedVisits(session, unassignedSearch) : []),
    [session, unassignedSearch],
  );
  useEffect(() => {
    const unregister = registerOpenUnassignedPanel(() =>
      setUnassignedOpen(true),
    );
    return () => {
      unregister();
      resetMoveToUnassignedConfirm();
    };
  }, [registerOpenUnassignedPanel, resetMoveToUnassignedConfirm]);
  const previousSessionId = useRef(session?.id);
  useEffect(() => {
    if (session?.id === previousSessionId.current) return;
    previousSessionId.current = session?.id;
    // A new generation can leave properties unassigned after the sidebar was closed.
    setUnassignedOpen(opensUnassigned(session));
    setUnassignedSearch("");
  }, [session]);
  const sortableParentRef = useRef<Element | null>(null);

  const itemsByRoute = useMemo(() => {
    const map: Record<string, string[]> = {};
    if (!session) return map;
    for (const route of session.routes) {
      map[route.id] = route.stops.map(
        (stop) => `${route.id}:${stop.propertyVisitId}`,
      );
    }
    map[UNASSIGNED_ROUTE_ID] = poolVisits.map(
      (visit) => `${UNASSIGNED_ROUTE_ID}:${visit.propertyVisitId}`,
    );
    return map;
  }, [session, poolVisits]);

  if (!session) return null;

  const isList = viewMode === "list";
  const isSplit = viewMode === "split";
  const showMap = viewMode === "map" || isSplit;
  const unroutedCount = session.unroutedJobs.length;
  const propertyVisitCount =
    session.routes.reduce((count, route) => count + route.stops.length, 0) +
    session.unroutedJobs.length;
  const editExpirations = new Set(
    Object.values(session.routePersistence ?? {}).map(
      (metadata) => metadata.expiresAt ?? "",
    ),
  );
  const hasMixedEditExpirations =
    session.builderMode === "edit" && editExpirations.size > 1;
  const expirationIso = hasMixedEditExpirations
    ? null
    : session.expiresAt ||
      (session.inputs.routeType === "temporary"
        ? session.inputs.expiresAt
        : undefined);

  const moveToRoute = (
    fromRouteId: string,
    toRouteId: string,
    propertyVisitId: string,
    toIndex: number,
  ) => {
    movePropertyVisit(fromRouteId, toRouteId, propertyVisitId, toIndex);
    if (toRouteId !== fromRouteId && toRouteId !== UNASSIGNED_ROUTE_ID) {
      setSelectedRouteId(toRouteId);
    }
  };

  const maybeReorderRouteColumn = (event: DragEndEvent) => {
    const source = event.operation?.source;
    if (source?.type !== ROUTE_BOARD_COLUMN_SORT_TYPE) return false;
    const routeId = source.data?.routeId;
    if (!routeId || !session) return true;

    // Drop target is the other column's droppable (type route-column), which
    // accepts column drags. Resolve by routeId — not sortable index.
    const targetRouteId = event.operation?.target?.data?.routeId;
    if (!targetRouteId || targetRouteId === routeId) return true;

    const toFullIndex = session.routes.findIndex(
      (route) => route.id === targetRouteId,
    );
    if (toFullIndex < 0) return true;

    reorderRoutes(routeId, toFullIndex);
    return true;
  };

  const maybeMoveDraggedProperty = (event: DragEndEvent) => {
    // Handle column drags separately and stop them from falling through to property movement.
    if (maybeReorderRouteColumn(event)) return;

    const source = event.operation?.source;
    const propertyVisitId =
      (source?.data as { propertyVisitId?: string } | undefined)
        ?.propertyVisitId ?? parseStopId(source?.id)?.propertyVisitId;
    if (!propertyVisitId) return;

    // The gap the "Drop here" line points at, in the target list as it is now
    // (see getStopDropPreviewIndex). Every drop lands there.
    const target = event.operation?.target;
    const targetData = target?.data as
      | { routeId?: string; index?: number; propertyVisitId?: string }
      | undefined;
    const dropRouteId = targetData?.routeId;
    if (typeof dropRouteId !== "string") return;
    let gapIndex: number;
    if (target?.type === "stop-slot" && typeof targetData?.index === "number") {
      gapIndex = targetData.index;
    } else if (
      target?.type === "stop" &&
      typeof targetData?.propertyVisitId === "string"
    ) {
      gapIndex = Math.max(
        0,
        itemsByRoute[dropRouteId]?.findIndex((id) =>
          id.endsWith(`:${targetData.propertyVisitId}`),
        ) ?? 0,
      );
    } else if (target?.type === "route-column") {
      gapIndex = itemsByRoute[dropRouteId]?.length ?? 0;
    } else {
      return;
    }

    const currentRouteId = locatePropertyVisit(
      session,
      propertyVisitId,
    )?.routeId;
    if (!currentRouteId) return;
    const targetIds =
      dropRouteId === UNASSIGNED_ROUTE_ID
        ? session.unroutedJobs.map((job) => job.propertyVisitId)
        : session.routes
            .find((route) => route.id === dropRouteId)
            ?.stops.map((stop) => stop.propertyVisitId) ?? [];
    const visibleTargetIds = (itemsByRoute[dropRouteId] ?? []).map(
      (id) => parseStopId(id)!.propertyVisitId,
    );
    const currentIndex = targetIds.indexOf(propertyVisitId);
    const toIndex = getStopDropIndex(
      targetIds,
      visibleTargetIds,
      propertyVisitId,
      gapIndex,
    );
    if (dropRouteId === currentRouteId && toIndex === currentIndex) return;
    moveToRoute(currentRouteId, dropRouteId, propertyVisitId, toIndex);
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-xs text-slate-600 sm:px-4">
        <SegmentedControl<ViewMode>
          value={viewMode}
          ariaLabel="View mode"
          options={[
            { value: "split", label: "Split", testId: "view-mode-split" },
            { value: "list", label: "List", testId: "view-mode-list" },
            { value: "map", label: "Map", testId: "view-mode-map" },
          ]}
          onChange={setViewMode}
        />
        <div
          className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500"
          data-testid="route-builder-header-metadata"
        >
          <span className="capitalize">
            {session.inputs.routeType} · {session.inputs.season}
          </span>
          {/* TODO: Restore dispatch metadata when planned dispatch time is
              exposed in the Route Builder UI again.
          <span>
            Dispatch:{" "}
            {new Date(session.inputs.plannedDispatchTime).toLocaleString()}
          </span>
          */}
          {hasMixedEditExpirations ? (
            <span data-testid="route-builder-expires">
              Expires: varies by route
            </span>
          ) : expirationIso ? (
            <span data-testid="route-builder-expires">
              Expires: {new Date(expirationIso).toLocaleString()}
            </span>
          ) : null}
          {session.inputs.routeType === "temporary" ? (
            <span
              className="inline-flex items-center gap-1 font-medium text-slate-600"
              data-testid="temporary-urgency-legend"
            >
              <span
                className="inline-block h-2.5 w-2.5 rounded-full bg-violet-600 ring-2 ring-red-500"
                aria-hidden
              />
              Recent service: purple badge
            </span>
          ) : null}
          <span>
            {session.routes.length} routes · {propertyVisitCount} property
            visits
          </span>
          {isBusy ? (
            <span
              className="font-medium text-sky-700"
              data-testid="builder-busy"
            >
              Working…
            </span>
          ) : null}
          {isRefreshingProperties ? (
            <span className="font-medium text-sky-700">
              Refreshing properties…
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {dirtyRouteIds.size > 1 ? (
            <button
              type="button"
              data-testid="reoptimize-all-button"
              disabled={isBusy || optimizationBlocked}
              className="rounded-lg border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => void reoptimizeAll()}
            >
              Re-optimize {dirtyRouteIds.size} routes
            </button>
          ) : null}
          <button
            type="button"
            data-testid="unassigned-toggle-button"
            aria-expanded={unassignedOpen}
            className={`inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-100 px-2.5 py-1.5 text-xs font-semibold text-amber-900 transition hover:bg-amber-200 ${unassignedOpen ? "ring-2 ring-amber-300 ring-offset-1" : ""}`}
            onClick={() => setUnassignedOpen((v) => !v)}
          >
            <img
              src={WarningTriangleIcon}
              alt=""
              className="h-3.5 w-3.5"
              aria-hidden
            />
            {unassignedOpen ? "Hide" : "Show"} Unassigned
            <span
              data-testid="unrouted-warning"
              className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white"
            >
              {unroutedCount}
            </span>
          </button>
        </div>
      </div>

      <RouteFocusBar />

      <div
        className={`flex min-h-0 flex-1 overflow-hidden ${
          viewMode === "map" ? "flex-row" : "flex-col md:flex-row"
        }`}
        data-testid="adjust-workspace"
      >
        <DragDropProvider
          onDragOver={(event) => {
            // OptimisticSortingPlugin moves sortable nodes in the live DOM.
            // Nested column+stop sortables crash (HierarchyRequestError); stop
            // moves also break React ownership. Block DOM moves; persist on drop.
            event.preventDefault();
          }}
          onDragStart={(event) => {
            sortableParentRef.current =
              event.operation.source?.element?.parentElement ?? null;
          }}
          onDragEnd={(event) => {
            const sourceElement = event.operation.source?.element;
            const originalParent = sortableParentRef.current;
            sortableParentRef.current = null;
            if (
              sourceElement &&
              originalParent &&
              sourceElement.parentElement !== originalParent
            ) {
              originalParent.appendChild(sourceElement);
            }
            if (event.canceled) return;
            maybeMoveDraggedProperty(event);
          }}
        >
          {isList ? (
            <div className="flex h-full min-h-0 min-w-0 flex-1 overflow-hidden">
              <ListBoardView />
            </div>
          ) : isSplit ? (
            <div className="flex h-[45%] min-h-[18rem] min-w-0 shrink-0 flex-col md:h-full md:min-h-0 md:w-[min(100%,520px)] md:flex-row md:overflow-hidden">
              <RoutesSidebar />
              <SelectedRoutePanel />
            </div>
          ) : null}

          {showMap ? (
            <div
              className={`relative min-h-0 min-w-0 flex-1 overflow-hidden ${
                viewMode === "map" ? "h-full" : "h-[55%] md:h-full"
              }`}
              data-testid="route-builder-map-pane"
            >
              <RouteBuilderMap
                showUnassignedMarkers={unassignedOpen}
                unassignedVisits={poolVisits}
              />
            </div>
          ) : null}

          {unassignedOpen ? (
            <aside
              className="h-72 shrink-0 border-t border-slate-200 bg-white p-2 md:h-auto md:w-80 md:border-l md:border-t-0"
              data-testid="unassigned-panel"
            >
              <UnassignedPool
                key={session.id}
                search={unassignedSearch}
                onSearch={setUnassignedSearch}
              />
            </aside>
          ) : null}
        </DragDropProvider>
      </div>

      {isBusy ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/40">
          <LoadingIcon fixed={false} size={42} />
        </div>
      ) : null}
    </div>
  );
}
